import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AgentArtifact } from '../../types/agent';
import { agentApi, apiErrorMessage } from '../../services/agentApi';
import { ARTIFACT_ICON, ARTIFACT_LABEL, artifactRoute, fmtDateTime, EmptyState, Spinner, card } from './agentUi';

const TYPES = ['', 'report', 'certificate', 'acord_form', 'filled_template', 'email_draft', 'pdf', 'writeup', 'document', 'folder', 'customer'];

const ArtifactGrid: React.FC<{ onOpenRun: (runId: string, artifactId?: string) => void }> = ({ onOpenRun }) => {
  const navigate = useNavigate();
  const [type, setType] = useState('');
  const [items, setItems] = useState<AgentArtifact[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await agentApi.artifacts({ type: type || undefined });
      setItems(r.artifacts);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load artifacts'));
    }
  }, [type]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        {TYPES.map((t) => (
          <button key={t || 'all'} onClick={() => setType(t)} className={`text-xs px-2.5 py-1 rounded-full border ${type === t ? 'border-primary text-primary bg-primary/5' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}>
            {t ? `${ARTIFACT_ICON[t]} ${ARTIFACT_LABEL[t]}` : 'All'}
          </button>
        ))}
      </div>
      {error && <p className="text-sm text-red-600 dark:text-red-400 mb-2">{error}</p>}
      {items === null ? (
        <div className="flex items-center gap-2 text-gray-500 py-10 justify-center"><Spinner /> Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState title="No artifacts yet">Reports, filled templates and drafted emails produced by Arie collect here.</EmptyState>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((a) => {
            const route = artifactRoute(a);
            return (
              <div key={a.id} className={`${card} p-3 flex flex-col`}>
                <div className="text-xs text-gray-500 dark:text-gray-400">{ARTIFACT_ICON[a.type]} {ARTIFACT_LABEL[a.type] || a.type} · {fmtDateTime(a.createdAt)}</div>
                <div className="text-sm font-medium text-gray-900 dark:text-white mt-1 break-words flex-1">{a.title}</div>
                {a.status !== 'ready' && <div className={`text-xs mt-1 ${a.status === 'failed' ? 'text-red-600' : 'text-amber-600'}`}>{a.status}</div>}
                <div className="flex gap-3 mt-2 text-xs">
                  {route && <button className="text-primary hover:underline" onClick={() => navigate(route)}>Open</button>}
                  <button className="text-primary hover:underline" onClick={() => onOpenRun(a.runId, a.id)}>View in run</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ArtifactGrid;
